import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../auth/useAuth';
import logo from '../../assets/default-if-logo.png';
import '../../styles/Checkout.css';
import API_BASE from '../../utils/api.js';

function getPlanPrice(plan, billing) {
  if (plan === 'standard') return billing === 'annual' ? 810 : 75;
  if (plan === 'premium') return billing === 'annual' ? 1350 : 125;
  return 0;
}

function calcDiscountedPrice(basePrice, promo) {
  if (!promo) return null;
  if (promo.percentOff) return +(basePrice * (1 - promo.percentOff / 100)).toFixed(2);
  if (promo.amountOff) return Math.max(0, +(basePrice - promo.amountOff / 100).toFixed(2));
  return null;
}

export default function Checkout() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { auth, setOnboardingStatus, setPlan } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [couponInput, setCouponInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const hasCompletedCheckout = useRef(false);

  const planData = JSON.parse(localStorage.getItem('signupPlan'));

  if (!planData && !hasCompletedCheckout.current) {
    navigate('/plans', { replace: true });
    return null;
  }

  const basePrice = getPlanPrice(planData?.plan, planData?.billing);
  const discountedPrice = calcDiscountedPrice(basePrice, appliedPromo);
  const priceSuffix = planData?.billing === 'annual' ? '/yr' : '/mo';

  async function handleApplyPromo() {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    setAppliedPromo(null);

    try {
      const res = await fetch(`${API_BASE}/api/stripe/validate-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth?.idToken ? { Authorization: `Bearer ${auth.idToken}` } : {}),
        },
        body: JSON.stringify({ code: couponInput.trim() }),
      });

      const data = await res.json();
      if (!res.ok) {
        setCouponError(data.error || 'Invalid promo code');
      } else {
        setAppliedPromo(data);
      }
    } catch {
      setCouponError('Failed to validate promo code');
    } finally {
      setCouponLoading(false);
    }
  }

  function handleRemovePromo() {
    setAppliedPromo(null);
    setCouponInput('');
    setCouponError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create subscription (with promo if applied)
      const subRes = await fetch(`${API_BASE}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth?.idToken ? { Authorization: `Bearer ${auth.idToken}` } : {}),
        },
        body: JSON.stringify({
          plan: planData.plan,
          billing: planData.billing,
          promotionCodeId: appliedPromo?.promotionCodeId || null,
        }),
      });

      const subData = await subRes.json();
      if (!subRes.ok) {
        throw new Error(subData.error || 'Failed to initialize subscription');
      }

      // Step 2: Confirm card payment (skipped when promo covers 100% of cost)
      if (subData.clientSecret) {
        const card = elements.getElement(CardElement);
        const result = await stripe.confirmCardPayment(subData.clientSecret, {
          payment_method: { card },
        });

        if (result.error) {
          setError(result.error.message);
          setLoading(false);
          return;
        }
      }

      // Step 3: Activate account. The server reads the plan and payment off the
      // subscription itself; plan/billing are only still sent so a backend
      // deployed after this frontend keeps accepting the request meanwhile.
      const onboardRes = await fetch(`${API_BASE}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
        body: JSON.stringify({
          plan: planData.plan,
          billing: planData.billing,
          subscriptionId: subData.subscriptionId,
        }),
      });

      const onboardData = await onboardRes.json().catch(() => ({}));
      if (!onboardRes.ok) {
        throw new Error(onboardData.error || 'Payment succeeded, but account activation failed.');
      }

      hasCompletedCheckout.current = true;
      setOnboardingStatus('completed');
      // /api/auth/me returned plan: null at login (the Settings row had no plan
      // until the call above wrote it), so auth.plan is still null. Without this
      // the whole post-signup session reads as Standard — Billing, the
      // QuickBooks tab and Time Billing all gate on auth.plan. The server's
      // answer wins: it's the plan the subscription actually pays for.
      setPlan(onboardData.plan || planData.plan);
      localStorage.removeItem('signupPlan');
      navigate('/settings/company', { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <>
      <img src={logo} alt="InteriorFolio" className="checkout-floating-logo" />

      <div className="checkout-page">
        <Link to="/plans" className="checkout-back-link">← Back to Plans</Link>
        <h1>Complete Your Subscription</h1>

        <div className="checkout-card">
          <div className="checkout-summary">
            <h3>{planData?.plan === 'premium' ? 'Premium Plan' : 'Standard Plan'}</h3>
            <p>{planData?.billing === 'annual' ? 'Annual Billing' : 'Monthly Billing'}</p>
            {discountedPrice !== null ? (
              <div className="checkout-pricing">
                <span className="checkout-price-original">${basePrice}{priceSuffix}</span>
                <strong className="checkout-price-discounted">${discountedPrice}{priceSuffix}</strong>
                <span className="checkout-discount-badge">{appliedPromo.name}</span>
              </div>
            ) : (
              <strong>${basePrice}{priceSuffix}</strong>
            )}
          </div>

          {!appliedPromo ? (
            <div className="checkout-coupon">
              <div className="checkout-coupon-row">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => { setCouponInput(e.target.value); setCouponError(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyPromo())}
                  placeholder="Promo code"
                  className="checkout-coupon-input"
                  disabled={couponLoading}
                />
                <button
                  type="button"
                  className="checkout-coupon-btn"
                  onClick={handleApplyPromo}
                  disabled={couponLoading || !couponInput.trim()}
                >
                  {couponLoading ? '…' : 'Apply'}
                </button>
              </div>
              {couponError && <p className="checkout-coupon-error">{couponError}</p>}
            </div>
          ) : (
            <div className="checkout-coupon-applied">
              <span>✓ {appliedPromo.name} applied</span>
              <button type="button" onClick={handleRemovePromo}>Remove</button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#111',
                    '::placeholder': { color: '#9ca3af' },
                  },
                },
              }}
            />

            {error && <div className="checkout-error">{error}</div>}

            <button type="submit" disabled={!stripe || loading}>
              {loading ? 'Processing…' : 'Confirm Payment'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
